import { useCallback, useEffect, useState } from "react";

import { useFormFillContext } from "../DocumentView/FormFillContext";
import {
  useFormFileTransport,
  useUploadActivity,
  type ResolvedFile,
} from "../DocumentView/FormFileTransportContext";
import { downscaleImageForUpload } from "./downscaleImage";

/**
 * Generic upload + file-resolve hooks for the file/media element
 * renderers. All UI state lives here; the actual network calls are
 * delegated to the host-injected `FormFileTransport`, so the public
 * and authenticated hosts share this logic.
 */

export interface FileRef {
  fileId: number;
  name: string;
}

/**
 * Whether a dropped File satisfies an `accept` attribute string, so drag-and-
 * drop honours the same filter as the native file picker. Handles the three
 * accept token forms: extension (`.pdf`), wildcard mime (`image/*`) and exact
 * mime (`image/png`). An empty accept string allows everything.
 */
export function fileMatchesAccept(file: File, accept: string): boolean {
  const tokens = accept
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  if (tokens.length === 0) return true;
  const mime = (file.type || "").toLowerCase();
  const name = file.name.toLowerCase();
  return tokens.some((tok) => {
    if (tok.startsWith(".")) return name.endsWith(tok);
    if (tok.endsWith("/*")) return mime.startsWith(tok.slice(0, -1));
    return mime !== "" && mime === tok;
  });
}

export interface PendingUpload {
  uploadId: string;
  file: File;
  state: "uploading" | "error";
  progress: number;
  errorMessage?: string;
}

interface UploadFileArgs {
  file: File;
  signedUrl: string;
  contentType?: string;
  onProgress?: (ev: ProgressEvent) => void;
}

/** XHR PUT to the presigned (S3) URL — kept inline so the package has no
 * attachment-lib dependency. */
function s3Put({ file, signedUrl, contentType, onProgress }: UploadFileArgs) {
  return new Promise<unknown>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", signedUrl, true);
    xhr.setRequestHeader("Content-Type", contentType ?? file.type);
    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve(xhr.response);
        return;
      }
      reject(new Error(`Upload failed (HTTP ${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable && onProgress) onProgress(ev);
    };
    xhr.send(file);
  });
}

interface UseFormResultUploadResult {
  pending: PendingUpload[];
  pickFiles: (files: File[]) => Promise<FileRef[]>;
  clearPending: (uploadId: string) => void;
}

export function useFormResultUpload(): UseFormResultUploadResult {
  const { formDocumentResultId } = useFormFillContext();
  const transport = useFormFileTransport();
  const activity = useUploadActivity();
  const [pending, setPending] = useState<PendingUpload[]>([]);

  const setPendingProgress = useCallback(
    (uploadId: string, progress: number) => {
      setPending((prev) =>
        prev.map((p) => (p.uploadId === uploadId ? { ...p, progress } : p)),
      );
    },
    [],
  );

  const markFailed = useCallback((uploadId: string, message: string) => {
    setPending((prev) =>
      prev.map((p) =>
        p.uploadId === uploadId
          ? { ...p, state: "error", errorMessage: message }
          : p,
      ),
    );
  }, []);

  const removePending = useCallback((uploadId: string) => {
    setPending((prev) => prev.filter((p) => p.uploadId !== uploadId));
  }, []);

  const pickFilesInner = useCallback(
    async (files: File[]): Promise<FileRef[]> => {
      if (files.length === 0) return [];
      if (!formDocumentResultId) {
        // The result row isn't ready yet. Give the host a chance to create it
        // on demand (the public host lazily starts its draft on the first
        // upload) before giving up; the transport resolves the freshly-created
        // id itself, so we don't need to wait for the context to re-render.
        // Only surface the inline notice when it genuinely can't be made ready.
        const ready = transport.ensureReady
          ? await transport.ensureReady()
          : false;
        if (!ready) {
          setPending((prev) => [
            ...prev,
            {
              uploadId: `notready-${Date.now()}`,
              file: files[0],
              state: "error",
              progress: 0,
              errorMessage: "Form is still starting up. Try again in a moment.",
            },
          ]);
          return [];
        }
      }

      const seeded: PendingUpload[] = files.map((file, idx) => ({
        uploadId: `${Date.now()}-${idx}-${file.name}`,
        file,
        state: "uploading",
        progress: 0,
      }));
      setPending((prev) => [...prev, ...seeded]);

      const refs: FileRef[] = [];
      for (const item of seeded) {
        try {
          // Downscale large images before uploading — the stored "original" is
          // already a sensible size (cap 2048px / q82); non-images pass through.
          const uploadFile = await downscaleImageForUpload(item.file);
          const { signedUrl, fileId, name } =
            await transport.createPresignedUpload({
              filename: uploadFile.name,
              contentType: uploadFile.type || null,
              sizeBytes: uploadFile.size || null,
            });
          if (!signedUrl || !fileId) {
            markFailed(item.uploadId, "Server did not return an upload URL");
            continue;
          }
          await s3Put({
            file: uploadFile,
            signedUrl,
            contentType: uploadFile.type || undefined,
            onProgress: (ev) => {
              if (ev.lengthComputable && ev.total > 0) {
                setPendingProgress(
                  item.uploadId,
                  Math.round((ev.loaded / ev.total) * 100),
                );
              }
            },
          });
          refs.push({ fileId: Number(fileId), name: name || uploadFile.name });
          // Kick off preview-thumbnail generation (fire-and-forget).
          transport.onUploaded?.(Number(fileId));
          removePending(item.uploadId);
        } catch (err) {
          markFailed(
            item.uploadId,
            err instanceof Error ? err.message : "Upload failed",
          );
        }
      }
      return refs;
    },
    [
      formDocumentResultId,
      transport,
      markFailed,
      removePending,
      setPendingProgress,
    ],
  );

  // The host hears about the whole pick-and-upload, however it ends.
  const pickFiles = useCallback(
    async (files: File[]): Promise<FileRef[]> => {
      activity.begin();
      try {
        return await pickFilesInner(files);
      } finally {
        activity.end();
      }
    },
    [pickFilesInner, activity],
  );

  return { pending, pickFiles, clearPending: removePending };
}

interface UseResolvedFileResult {
  file: ResolvedFile | null;
  loading: boolean;
}

interface UseResultFileResult extends UseResolvedFileResult {
  /**
   * The lookup finished and found nothing: the file was deleted, or this
   * reader may not have it. A renderer shows its unavailable state for this
   * instead of waiting on a file that is not coming.
   */
  missing: boolean;
}

/** Resolve a file uploaded into the current fill result. */
export function useResultFile(
  fileId: number | null,
  options: { download?: boolean } = {},
): UseResultFileResult {
  const transport = useFormFileTransport();
  const download = options.download ?? false;
  // Seed synchronously so a server-render (PDF) shows the image in its single
  // pass — the useEffect below never runs under renderToStringWithData.
  const [file, setFile] = useState<ResolvedFile | null>(() =>
    fileId ? transport.peekFile?.(fileId, { download }) ?? null : null,
  );
  const [loading, setLoading] = useState(false);
  // A server render never runs the effect below, so what the host could peek
  // is all it will ever have: the lookup is already over.
  const [settled, setSettled] = useState(() => !!fileId && !!transport.peekFile);

  useEffect(() => {
    if (!fileId) {
      setFile(null);
      setSettled(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setSettled(false);
    transport
      .fetchResultFileUrl(fileId, { download })
      .then((f) => {
        if (!cancelled) setFile(f);
      })
      .catch(() => {
        if (!cancelled) setFile(null);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setSettled(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fileId, download, transport]);

  return { file, loading, missing: settled && !file };
}

/** Resolve a file the form designer attached at design time. */
export function usePublicFile(
  fileId: number | null | undefined,
): UseResolvedFileResult {
  const transport = useFormFileTransport();
  // Seed synchronously for SSR (see useResultFile).
  const [file, setFile] = useState<ResolvedFile | null>(() =>
    fileId != null ? transport.peekFile?.(fileId) ?? null : null,
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (fileId == null) {
      setFile(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    transport
      .fetchPublicFileUrl(fileId)
      .then((f) => {
        if (!cancelled) setFile(f);
      })
      .catch(() => {
        if (!cancelled) setFile(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fileId, transport]);

  return { file, loading };
}
